import { useEffect, useState } from "react";
import { BarChart3, Globe2, RefreshCw, Loader2 } from "lucide-react";

import { AdminHeader } from "@/components/admin/AdminHeader";
import { adminApi } from "@/lib/adminApi";

// ------------ Mapping source technique → libellé humain -----------------
const SOURCE_LABELS = {
  poprock: "Pop Rock Radio",
  carla: "Carla Radio",
  dance: "Dance Premiere",
  majestic: "Majestic Jukebox",
  jazz: "Jazz Razzmatazz",
  italianostalgia: "Italia Nostalgia",
  hitalia: "HITALIA",
  powervibes: "Power Vibes",
  walltunes: "WallTunes (mur)",
  "marketing:home": "Site — Accueil",
  "marketing:contact": "Site — Contact",
  "marketing:offer-pwa": "Site — Offre PWA",
  "marketing:offer-stream": "Site — Offre Stream",
  "marketing:now-playing": "Site — Now Playing",
};

const humanSource = (s) => SOURCE_LABELS[s] || s;

// Convertit un code ISO2 en drapeau emoji ("FR" → 🇫🇷)
function flagEmoji(code) {
  if (!code || code.length !== 2 || code === "XX") return "🌐";
  const base = 0x1f1e6;
  return String.fromCodePoint(
    base + (code.charCodeAt(0) - 65),
    base + (code.charCodeAt(1) - 65)
  );
}

// ------------ Sous-composants -------------------------------------------
function RangeSelector({ range, onChange, disabled }) {
  const ranges = [
    { key: "24h", label: "24 h" },
    { key: "7d", label: "7 jours" },
    { key: "30d", label: "30 jours" },
  ];
  return (
    <div className="stats-range">
      {ranges.map((r) => (
        <button
          key={r.key}
          type="button"
          disabled={disabled}
          className={`stats-range-btn ${range === r.key ? "is-active" : ""}`}
          onClick={() => onChange(r.key)}
          data-testid={`stats-range-${r.key}`}
        >
          {r.label}
        </button>
      ))}
    </div>
  );
}

function StatCards({ total, sourceCount, countryCount }) {
  return (
    <div className="stats-cards">
      <div className="stats-card">
        <div className="stats-card-label">Visiteurs uniques</div>
        <div className="stats-card-value" data-testid="stats-total">{total}</div>
      </div>
      <div className="stats-card">
        <div className="stats-card-label">Apps / pages actives</div>
        <div className="stats-card-value">{sourceCount}</div>
      </div>
      <div className="stats-card">
        <div className="stats-card-label">Pays uniques</div>
        <div className="stats-card-value">{countryCount}</div>
      </div>
    </div>
  );
}

function SourceRow({ item }) {
  const total = item.visits;
  const top3 = item.countries.slice(0, 5);
  return (
    <div className="stats-source-row" data-testid={`stats-src-${item.source}`}>
      <div className="stats-source-name">{humanSource(item.source)}</div>
      <div className="stats-source-visits">{total}</div>
      <div className="stats-source-countries">
        {top3.map((c) => (
          <span key={c.code} className="stats-country-chip" title={c.country}>
            <span className="stats-flag">{flagEmoji(c.code)}</span>
            <span className="stats-country-name">{c.country}</span>
            <span className="stats-country-count">{c.count}</span>
          </span>
        ))}
        {item.countries.length > top3.length && (
          <span className="stats-country-more">+{item.countries.length - top3.length}</span>
        )}
      </div>
    </div>
  );
}

function CountryGlobalList({ items }) {
  if (!items.length) return null;
  return (
    <section className="stats-global">
      <div className="stats-global-title">
        <Globe2 size={14} /> Répartition globale par pays
      </div>
      <div className="stats-global-grid">
        {items.slice(0, 20).map((c) => (
          <div key={c.code} className="stats-global-row">
            <span className="stats-flag">{flagEmoji(c.code)}</span>
            <span className="stats-global-name">{c.country}</span>
            <span className="stats-global-count">{c.count}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

// ------------ Page ------------------------------------------------------
export default function AdminAnalyticsPage() {
  const [range, setRange] = useState("7d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async (r) => {
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi.analytics(r);
      setData(res);
    } catch (e) {
      setError(e.message || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats(range);
  }, [range]);

  return (
    <div className="admin-page" data-testid="admin-analytics-page">
      <AdminHeader />
      <main className="admin-main">
        <div className="admin-page-head">
          <div>
            <h1 className="admin-page-title">
              <BarChart3 size={22} style={{ marginBottom: -3, marginRight: 8 }} />
              Statistiques de visite
            </h1>
            <p className="admin-page-sub">
              Visiteurs uniques par PWA + WallTunes + site marketing.
              Anonymisé (IP hashée SHA-256), déduplication 30 min.
            </p>
          </div>
          <div className="admin-page-actions">
            <RangeSelector range={range} onChange={setRange} disabled={loading} />
            <button
              type="button"
              onClick={() => fetchStats(range)}
              disabled={loading}
              className="mw-btn mw-btn-ghost"
              data-testid="stats-refresh"
            >
              {loading ? <Loader2 size={14} className="mw-spin" /> : <RefreshCw size={14} />}
              Actualiser
            </button>
          </div>
        </div>

        {error && <div className="admin-error" data-testid="stats-error">{error}</div>}

        {data && (
          <>
            <StatCards
              total={data.total_unique_visits}
              sourceCount={data.by_source.length}
              countryCount={data.by_country_global.length}
            />

            <section className="stats-sources">
              <div className="stats-sources-head">
                <span>Source</span>
                <span>Visites</span>
                <span>Top pays</span>
              </div>
              {data.by_source.length === 0 ? (
                <div className="stats-empty">
                  Aucune visite sur cette période. Les données commencent à s&apos;accumuler
                  dès qu&apos;un visiteur ouvre une PWA, WallTunes ou une page marketing.
                </div>
              ) : (
                data.by_source.map((s) => <SourceRow key={s.source} item={s} />)
              )}
            </section>

            <CountryGlobalList items={data.by_country_global} />
          </>
        )}
      </main>
    </div>
  );
}
